(() => {
  const facetKeys = ['color', 'style', 'fit', 'jacket', 'waist', 'lapel', 'occasion'];
  const valueOrder = {
    color: ['black', 'white', 'gray', 'navy', 'blue', 'brown', 'tan', 'green', 'red', 'pink', 'purple', 'orange', 'yellow'],
    fit: ['Slim', 'Modern', 'Trim'],
    occasion: ['Wedding', 'Work', 'Prom', 'Black tie'],
    lapel: ['Notch', 'Peak', 'Shawl'],
    style: ['2-piece suit', '3-piece suit', 'Tuxedo']
  };
  const ordered = (key, values) => {
    const order = valueOrder[key] || [];
    return [...values].sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return String(a).localeCompare(String(b));
    });
  };
  const textList = (value) => Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim()) : [];
  const facetParams = new Set([...facetKeys.map((key) => `g_${key}`), 'g_availability', 'g_min', 'g_max', 'g_page']);
  const colorWords = {
    black: /\bblack\b/i,
    white: /\b(white|ivory|cream|bone)\b/i,
    blue: /\b(blue|navy|indigo|periwinkle|royal|teal)\b/i,
    gray: /\b(gr[ae]y|charcoal|silver)\b/i,
    brown: /\b(brown|tan|khaki|chocolate|camel)\b/i,
    green: /\b(green|emerald|sage|olive)\b/i,
    pink: /\b(pink|blush|petal|rose)\b/i,
    red: /\b(red|burgundy|crimson|maroon|wine)\b/i,
    purple: /\b(purple|plum|lavender|lilac)\b/i,
    orange: /\b(orange|rust|terracotta)\b/i,
    yellow: /\b(yellow|gold|mustard)\b/i
  };
  const normalizeProduct = (product) => {
    const options = Array.isArray(product.options) ? product.options : [];
    const find = (expression) => options.findIndex((option) => expression.test(option.name));
    const colorIndex = find(/^colou?r$/i);
    const colorSource = colorIndex >= 0 && Array.isArray(options[colorIndex].values) ? options[colorIndex].values.map((value) => typeof value === 'string' ? value : value.name || value.value || '').join(' ') : product.title;
    const catalogColors = textList(product.colors).map((color) => color.toLowerCase());
    return {
      ...product,
      colors: catalogColors.length ? catalogColors : Object.entries(colorWords).filter(([, expression]) => expression.test(colorSource)).map(([color]) => color),
      fits: textList(product.fits),
      occasions: textList(product.occasions),
      lapels: typeof product.lapel === 'string' && product.lapel.trim() ? [product.lapel.trim()] : [],
      jacketIndex: find(/^(jacket size|chest size|jacket chest)$/i),
      waistIndex: find(/^(pant size|pants size|pant waist|waist size)$/i)
    };
  };
  const readState = (url) => {
    const state = {page: Math.max(1, Number.parseInt(url.searchParams.get('g_page') || '1', 10) || 1)};
    for (const key of facetKeys) state[key] = url.searchParams.getAll(`g_${key}`).filter(Boolean);
    state.availability = url.searchParams.get('g_availability') || (url.searchParams.get('filter.v.availability') === '1' ? 'order' : url.searchParams.get('filter.v.availability') === '0' ? 'unavailable' : 'any');
    if (!['any', 'order', 'ready', 'preorder', 'unavailable'].includes(state.availability)) state.availability = 'any';
    for (const [key, native] of [['min', 'filter.v.price.gte'], ['max', 'filter.v.price.lte']]) {
      const value = url.searchParams.get(`g_${key}`) ?? url.searchParams.get(native);
      state[key] = value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;
    }
    return state;
  };
  const variantMatches = (product, variant, state) => {
    if (state.jacket.length && (product.jacketIndex < 0 || !state.jacket.includes(variant.options[product.jacketIndex]))) return false;
    if (state.waist.length && (product.waistIndex < 0 || !state.waist.includes(variant.options[product.waistIndex]))) return false;
    if (state.min !== null && variant.price < state.min * 100) return false;
    if (state.max !== null && variant.price > state.max * 100) return false;
    if ((state.availability === 'order' || (state.availability === 'any' && (state.jacket.length || state.waist.length))) && variant.state === 'unavailable') return false;
    if (state.availability !== 'any' && state.availability !== 'order' && variant.state !== state.availability) return false;
    return true;
  };
  const productMatches = (product, state) => {
    if (state.color.length && !state.color.some((value) => product.colors.includes(value))) return false;
    if (state.style.length && !state.style.includes(product.style)) return false;
    if (state.fit.length && !state.fit.some((value) => product.fits.includes(value))) return false;
    if (state.occasion.length && !state.occasion.some((value) => product.occasions.includes(value))) return false;
    if (state.lapel.length && !state.lapel.some((value) => product.lapels.includes(value))) return false;
    return product.variants.some((variant) => variantMatches(product, variant, state));
  };
  const stateUrl = (source, state) => {
    const url = new URL(source);
    for (const name of [...url.searchParams.keys()]) {
      if (facetParams.has(name) || name.startsWith('filter.') || name === 'page') url.searchParams.delete(name);
    }
    for (const key of facetKeys) for (const value of state[key]) url.searchParams.append(`g_${key}`, value);
    if (state.availability !== 'any') url.searchParams.set('g_availability', state.availability);
    if (state.min !== null) url.searchParams.set('g_min', String(state.min));
    if (state.max !== null) url.searchParams.set('g_max', String(state.max));
    if (state.page > 1) {
      url.searchParams.set('g_page', String(state.page));
      url.searchParams.set('page', String(state.page));
    }
    return url;
  };
  const validateBatch = (data, page, handle, expected, pages) => {
    if (data.version !== 1 || data.page !== page || data.collection !== handle || !Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages > 500 || data.pages < 0 || !Number.isInteger(data.total) || data.total < 0) throw new Error(`catalog shape ${JSON.stringify({version: data.version, page: data.page, requestedPage: page, collection: typeof data.collection === 'string' ? data.collection : typeof data.collection, expectedHandle: handle, pages: data.pages, total: data.total, productsArray: Array.isArray(data.products)})}`);
    if (expected !== null && (expected !== data.total || pages !== data.pages)) throw new Error('catalog changed');
    for (const product of data.products) {
      if (!product.id || product.complete !== true || !Array.isArray(product.variants) || typeof product.card !== 'string' || !Array.isArray(product.options)) throw new Error('product shape');
      for (const variant of product.variants) {
        if (!variant.id || !Array.isArray(variant.options) || !Number.isFinite(variant.price) || !['ready', 'preorder', 'order', 'unavailable'].includes(variant.state)) throw new Error('variant shape');
      }
    }
  };
  const validateComplete = (products, expected) => {
    if (products.length !== expected || new Set(products.map((product) => product.id)).size !== expected) throw new Error('incomplete catalog');
  };
  const cardCopy = (product, override) => {
    if (!override) return null;
    const displayName = typeof override.displayName === 'string' ? override.displayName.trim() : '';
    const descriptor = typeof override.descriptor === 'string' ? override.descriptor.trim() : '';
    return {displayName, descriptor, accessibleName: displayName && displayName !== product.title ? `${displayName} — ${product.title}` : product.title};
  };
  const api = {normalizeProduct, readState, variantMatches, productMatches, stateUrl, validateBatch, validateComplete, cardCopy};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;

  const element = (name, className, text) => {
    const node = document.createElement(name);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const interpolate = (label, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key}__`, value), label);
  const start = (root) => {
    if (root.dataset.gFacetsBound) return;
    root.dataset.gFacetsBound = 'true';
    const sourceLabels = root.querySelector('[data-g-facet-labels]');
    const form = root.querySelector('[data-g-filter-form]');
    const loading = root.querySelector('[data-g-facet-loading]');
    const status = root.querySelector('[data-g-facet-status]');
    const retry = root.querySelector('[data-g-facet-retry]');
    const dialog = form?.closest('dialog');
    const trigger = root.querySelector('.g-collection-filter [data-g-panel-trigger]');
    const count = root.querySelector('[data-g-result-count]');
    if (!sourceLabels || !form || !loading || !status || !retry || !dialog || !trigger || !count) return;
    const currentLocation = new URL(location.href);
    if ([...currentLocation.searchParams.keys()].some((name) => name.startsWith('filter.') && !['filter.v.availability', 'filter.v.price.gte', 'filter.v.price.lte'].includes(name))) return;
    let labels;
    try { labels = JSON.parse(sourceLabels.textContent); } catch { return; }
    const cardOverrides = new Map();
    try {
      const values = JSON.parse(root.querySelector('[data-g-card-copy-overrides]')?.textContent || '[]');
      if (Array.isArray(values)) for (const value of values) if (value && value.id) cardOverrides.set(String(value.id), value);
    } catch {}
    const pageSize = Math.max(6, Math.min(48, Number(root.dataset.pageSize) || 12));
    const baseUrl = new URL(root.dataset.collectionUrl, location.origin);
    if (currentLocation.pathname.startsWith(`${baseUrl.pathname}/`)) baseUrl.pathname = currentLocation.pathname;
    if (root.dataset.previewThemeId) baseUrl.searchParams.set('preview_theme_id', root.dataset.previewThemeId);
    if (root.dataset.templateView) baseUrl.searchParams.set('view', root.dataset.templateView);
    const originalForm = form.innerHTML;
    let products = null;
    let state = readState(new URL(location.href));
    let requestId = 0;
    let pending = null;
    let controller = null;
    let enhanced = false;
    let disposed = false;
    let autoRetried = false;
    const choices = {};
    let grid = root.querySelector('[data-g-facet-grid]');
    const empty = root.querySelector('[data-g-facet-empty]');
    const pagination = root.querySelector('[data-g-facet-pagination]');
    const chips = root.querySelector('[data-g-facet-chips]');

    const labelFor = (key, value) => key === 'color' ? labels.colors[value] || value : key === 'style' ? labels.styles[value] || value : key === 'fit' ? labels.fits?.[value] || value : key === 'occasion' ? labels.occasions?.[value] || value : key === 'lapel' ? labels.lapels?.[value] || value : key === 'availability' ? labels[value === 'order' ? 'available' : value] || value : value;
    const drawForm = () => {
      form.replaceChildren();
      const groups = element('div', 'g-collection-filter__groups');
      for (const key of [...facetKeys, 'availability']) {
        const values = key === 'availability' ? ['any', 'ready', 'preorder', 'order'] : choices[key];
        if (!values?.length) continue;
        const details = element('details', 'g-collection-filter-group');
        details.open = key === 'color' || key === 'jacket' || (key === 'availability' ? state.availability !== 'any' : state[key]?.length > 0);
        details.append(element('summary', '', labels[key]));
        const fieldset = element('fieldset', 'g-collection-filter-fieldset');
        fieldset.append(element('legend', 'g-visually-hidden', labels[key]));
        const list = element('ul', `g-collection-filter-values${key === 'jacket' || key === 'waist' ? ' g-collection-filter-values--sizes' : key === 'color' ? ' g-collection-filter-values--swatches' : ''}`);
        list.setAttribute('role', 'list');
        for (const value of values) {
          const li = element('li');
          const label = element('label', key === 'availability' ? 'g-collection-radio' : 'g-collection-checkbox');
          const input = element('input');
          input.type = key === 'availability' ? 'radio' : 'checkbox';
          input.name = `g_${key}`;
          input.value = value;
          input.checked = key === 'availability' ? state[key] === value : state[key].includes(value);
          input.dataset.facetKey = key;
          input.dataset.facetValue = value;
          if (key === 'color' && labels.swatches?.[value]) {
            const swatch = element('span', 'g-facet-swatch');
            swatch.setAttribute('aria-hidden', 'true');
            swatch.style.setProperty('--g-sw', labels.swatches[value]);
            label.append(input, swatch, element('span', '', labelFor(key, value)));
          } else {
            label.append(input, element('span', '', labelFor(key, value)));
          }
          const tally = element('span', 'g-muted');
          tally.dataset.facetTally = '';
          label.append(tally);
          li.append(label);
          list.append(li);
        }
        fieldset.append(list);
        if (key === 'fit' && labels.fitNote) fieldset.append(element('p', 'g-collection-filter-note', labels.fitNote));
        details.append(fieldset);
        groups.append(details);
      }
      const note = element('p', 'g-collection-filter-note', labels.sizeNote);
      groups.append(note);
      const price = element('fieldset', 'g-collection-filter-fieldset g-collection-facet-price');
      price.append(element('legend', '', `${labels.price} (${labels.currency})`));
      const ranges = element('div', 'g-collection-price-range');
      for (const key of ['min', 'max']) {
        const wrapper = element('div');
        const label = element('label', '', labels[key === 'min' ? 'from' : 'to']);
        const input = element('input');
        input.type = 'number';
        input.inputMode = 'decimal';
        input.min = '0';
        input.step = 'any';
        input.name = `g_${key}`;
        input.value = state[key] === null ? '' : String(state[key]);
        label.append(input);
        wrapper.append(label);
        ranges.append(wrapper);
      }
      price.append(ranges);
      groups.append(price);
      const footer = element('div', 'g-collection-filter__footer');
      const submit = element('button', 'g-button', labels.apply);
      submit.type = 'submit';
      const clear = element('button', 'g-collection-text-link', labels.clear);
      clear.type = 'button';
      clear.addEventListener('click', () => apply(readState(baseUrl)));
      footer.append(submit, clear);
      form.append(groups, footer);
      updateTallies();
    };
    const formState = () => {
      const next = {page: 1};
      const data = new FormData(form);
      for (const key of facetKeys) next[key] = data.getAll(`g_${key}`);
      next.availability = data.get('g_availability') || 'any';
      for (const key of ['min', 'max']) next[key] = data.get(`g_${key}`) === '' ? null : Number(data.get(`g_${key}`));
      return next;
    };
    const updateTallies = () => {
      if (!products) return;
      const draft = formState();
      for (const input of form.querySelectorAll('[data-facet-key]')) {
        const key = input.dataset.facetKey;
        const test = {...draft, [key]: key === 'availability' ? input.value : [input.value]};
        const total = products.filter((product) => productMatches(product, test)).length;
        input.parentElement.querySelector('[data-facet-tally]').textContent = `(${total})`;
        input.disabled = total === 0 && !input.checked;
      }
    };
    const syncSort = () => {
      for (const sortForm of root.querySelectorAll('[data-g-sort-form]')) {
        sortForm.querySelectorAll('[data-g-facet-sort-param]').forEach((input) => input.remove());
        sortForm.querySelectorAll('input[name^="filter."]').forEach((input) => input.remove());
        const url = stateUrl(baseUrl, {...state, page: 1});
        for (const [name, value] of url.searchParams) {
          if (!facetParams.has(name)) continue;
          const input = element('input');
          input.type = 'hidden'; input.name = name; input.value = value;
          input.dataset.gFacetSortParam = '';
          sortForm.append(input);
        }
      }
    };
    const render = () => {
      const filtered = products.filter((product) => productMatches(product, state));
      const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
      state.page = Math.min(state.page, pages);
      const shown = filtered.slice((state.page - 1) * pageSize, state.page * pageSize);
      if (!grid) {
        grid = element('div', 'g-collection-grid');
        grid.setAttribute('role', 'list');
        grid.dataset.gFacetGrid = '';
        empty.before(grid);
      }
      grid.replaceChildren();
      for (const product of shown) {
        const item = element('div');
        item.setAttribute('role', 'listitem');
        const template = document.createElement('template');
        template.innerHTML = product.card;
        const article = template.content.querySelector('article.g-collection-card');
        if (!article) continue;
        const copy = cardCopy(product, cardOverrides.get(String(product.id)));
        if (copy) {
          const heading = article.querySelector('.g-collection-card__title');
          const titleLink = heading?.querySelector('a');
          if (copy.displayName && titleLink) {
            titleLink.textContent = copy.displayName;
            titleLink.setAttribute('aria-label', copy.accessibleName);
          }
          if (copy.descriptor && heading) {
            let descriptor = article.querySelector('.g-collection-card__descriptor');
            if (!descriptor) {
              descriptor = element('p', 'g-collection-card__descriptor');
              heading.after(descriptor);
            }
            descriptor.textContent = copy.descriptor;
          }
        }
        const variant = product.variants.find((candidate) => variantMatches(product, candidate, state));
        if (variant && (state.jacket.length || state.waist.length || state.availability !== 'any')) {
          for (const link of article.querySelectorAll('a[href]')) {
            const url = new URL(link.href, location.origin);
            url.searchParams.set('variant', variant.id);
            link.href = url.href;
          }
        }
        item.append(article);
        grid.append(item);
      }
      grid.hidden = shown.length === 0;
      empty.hidden = shown.length > 0;
      root.querySelector('[data-g-native-empty]')?.setAttribute('hidden', '');
      root.querySelector('[data-g-native-pagination]')?.setAttribute('hidden', '');
      root.querySelector('[data-g-native-chips]')?.setAttribute('hidden', '');
      count.textContent = interpolate(labels.count, {count: filtered.length});
      chips.replaceChildren();
      let activeCount = 0;
      const addChip = (key, value, caption) => {
        const link = element('a', 'g-collection-chip');
        const next = {...state, page: 1};
        if (facetKeys.includes(key)) next[key] = state[key].filter((choice) => choice !== value);
        else if (key === 'availability') next.availability = 'any';
        else next[key] = null;
        link.href = stateUrl(location.href, next).href;
        link.setAttribute('aria-label', interpolate(labels.remove, {filter: labels[key] || labels.price, value: caption}));
        link.append(element('span', '', `${labels[key] || labels.price}: ${caption}`));
        const cross = element('span', '', '×'); cross.setAttribute('aria-hidden', 'true'); link.append(cross);
        link.addEventListener('click', (event) => { if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {event.preventDefault(); apply(next);} });
        chips.append(link);
        activeCount += 1;
      };
      for (const key of facetKeys) for (const value of state[key]) addChip(key, value, labelFor(key, value));
      if (state.availability !== 'any') addChip('availability', state.availability, labelFor('availability', state.availability));
      if (state.min !== null) addChip('min', state.min, `${labels.from} ${state.min} ${labels.currency}`);
      if (state.max !== null) addChip('max', state.max, `${labels.to} ${state.max} ${labels.currency}`);
      if (activeCount) {
        const clear = element('button', 'g-collection-text-link', labels.clear);
        clear.type = 'button'; clear.addEventListener('click', () => apply(readState(baseUrl))); chips.append(clear);
      }
      chips.hidden = activeCount === 0;
      const triggerLabel = trigger.querySelector('span');
      if (triggerLabel) triggerLabel.textContent = `${root.dataset.filterLabel || triggerLabel.textContent.replace(/\s*\(\d+\)$/, '')}${activeCount ? ` (${activeCount})` : ''}`;
      pagination.replaceChildren();
      pagination.hidden = pages <= 1;
      if (pages > 1) {
        pagination.append(element('p', '', interpolate(labels.page, {current: state.page, total: pages})));
        const links = element('div', 'g-collection-pagination__links');
        const addPage = (number, caption, button = false) => {
          if (number === state.page && !button) {
            const current = element('span', 'g-collection-page-number', caption); current.setAttribute('aria-current', 'page'); links.append(current); return;
          }
          const link = element('a', button ? 'g-button g-button--outline' : 'g-collection-page-number', caption);
          const next = {...state, page: number};
          link.href = stateUrl(location.href, next).href;
          if (!button) link.setAttribute('aria-label', interpolate(labels.goPage, {number}));
          link.addEventListener('click', (event) => {
            if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault(); apply(next); count.tabIndex = -1; count.focus({preventScroll: true}); count.scrollIntoView({block: 'start'});
          });
          links.append(link);
        };
        if (state.page > 1) addPage(state.page - 1, labels.previous, true);
        for (let page = 1; page <= pages; page += 1) {
          if (page === 1 || page === pages || Math.abs(page - state.page) <= 1) addPage(page, String(page));
          else if (page === state.page - 2 || page === state.page + 2) links.append(element('span', 'g-collection-page-gap', '…'));
        }
        if (state.page < pages) addPage(state.page + 1, labels.next, true);
        pagination.append(links);
      }
      syncSort();
    };
    const apply = (next, push = true) => {
      state = next;
      render();
      if (push) history.pushState({giorgioFacets: true}, '', stateUrl(location.href, state));
      drawForm();
      dialog.close();
    };
    const hasFacetParams = () => [...new URL(location.href).searchParams.keys()].some((name) => facetParams.has(name));
    const load = async (show = true) => {
      if (products) return products;
      if (pending) return pending;
      const id = ++requestId;
      controller = new AbortController();
      loading.hidden = false; retry.hidden = true; status.textContent = labels.loading;
      form.setAttribute('aria-busy', 'true');
      pending = (async () => {
        const collected = [];
        let pages = 1;
        let expected = null;
        for (let page = 1; page <= pages; page += 1) {
          const url = new URL(baseUrl);
          url.searchParams.set('view', 'giorgio-facets');
          url.searchParams.set('sort_by', new URL(location.href).searchParams.get('sort_by') || root.dataset.defaultSort || 'manual');
          url.searchParams.set('page', String(page));
          const timeout = setTimeout(() => controller.abort(), 25000);
          let data;
          try {
            const response = await fetch(url, {credentials: 'same-origin', signal: controller.signal, headers: {Accept: 'text/html'}});
            if (!response.ok) throw new Error('catalog response');
            data = await response.json();
          } finally { clearTimeout(timeout); }
          validateBatch(data, page, root.dataset.collectionUrl.split('/').filter(Boolean).pop(), expected, pages);
          expected = data.total; pages = data.pages;
          for (const product of data.products) {
            collected.push(normalizeProduct(product));
          }
        }
        validateComplete(collected, expected);
        if (id !== requestId || disposed) return null;
        products = collected;
        choices.color = ordered('color', new Set(products.flatMap((product) => product.colors)));
        choices.fit = ordered('fit', new Set(products.flatMap((product) => product.fits)));
        choices.occasion = ordered('occasion', new Set(products.flatMap((product) => product.occasions)));
        choices.style = ordered('style', new Set(products.map((product) => product.style).filter(Boolean)));
        choices.lapel = ordered('lapel', new Set(products.flatMap((product) => product.lapels)));
        if (choices.lapel.length < 2) choices.lapel = [];
        for (const key of ['jacket', 'waist']) choices[key] = [...new Set(products.flatMap((product) => product[`${key}Index`] < 0 ? [] : product.variants.map((variant) => variant.options[product[`${key}Index`]])))].sort((a, b) => a.localeCompare(b, undefined, {numeric: true}));
        root.dataset.filterLabel = trigger.querySelector('span')?.textContent.replace(/\s*\(\d+\)$/, '') || '';
        enhanced = true; root.dataset.gFacetsReady = 'true';
        loading.hidden = true; form.removeAttribute('aria-busy');
        const hadFormFocus = form.contains(document.activeElement);
        drawForm();
        if (hadFormFocus) form.querySelector('input:not(:disabled)')?.focus({preventScroll: true});
        if (show || hasFacetParams()) render();
        if ([...new URL(location.href).searchParams.keys()].some((name) => facetParams.has(name))) history.replaceState({giorgioFacets: true}, '', stateUrl(location.href, state));
        return products;
      })().catch((error) => {
        if (id !== requestId || disposed) return null;
        if (root.dataset.previewThemeId) console.warn('MenSuits detailed filters:', error.message);
        if (!autoRetried) {
          // One quiet retry covers a dropped or slow mobile connection before the shopper sees an error.
          autoRetried = true;
          pending = null;
          return new Promise((resolve) => setTimeout(resolve, 1200)).then(() => load(show));
        }
        products = null;
        loading.hidden = false; retry.hidden = false; status.textContent = labels.failed;
        form.removeAttribute('aria-busy');
        if (!enhanced) form.innerHTML = originalForm;
        return null;
      }).finally(() => {if (id === requestId) pending = null;});
      return pending;
    };
    trigger.addEventListener('click', () => {load(false);});
    for (const intent of ['pointerenter', 'focus', 'touchstart']) trigger.addEventListener(intent, () => {load(false);}, {once: true, passive: true});
    const connection = navigator.connection;
    if (!hasFacetParams() && window.matchMedia('(pointer: fine)').matches && !(connection && (connection.saveData || (connection.effectiveType && connection.effectiveType !== '4g')))) {
      const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 2500));
      idle(() => {if (!products && !pending) load(false);}, {timeout: 5000});
    }
    retry.addEventListener('click', () => {load();});
    form.addEventListener('change', () => {if (enhanced) updateTallies();});
    form.addEventListener('input', () => {
      if (!enhanced) return;
      const next = formState();
      form.elements.namedItem('g_max').setCustomValidity(next.min !== null && next.max !== null && next.min > next.max ? labels.invalidRange : '');
    });
    form.addEventListener('submit', (event) => {
      if (!enhanced) return;
      event.preventDefault(); root.removeAttribute('aria-busy');
      if (form.reportValidity()) apply(formState());
    });
    root.querySelector('[data-g-facet-reset]')?.addEventListener('click', () => apply(readState(baseUrl)));
    window.addEventListener('popstate', async () => {
      const next = readState(new URL(location.href));
      if (products || await load()) {state = next; drawForm(); render();}
    });
    window.addEventListener('pagehide', () => {disposed = true; requestId += 1; controller?.abort();});
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      disposed = false; pending = null;
      if (!products && (dialog.open || [...new URL(location.href).searchParams.keys()].some((name) => facetParams.has(name)))) load();
    });
    if ([...new URL(location.href).searchParams.keys()].some((name) => facetParams.has(name))) load();
  };
  const init = (scope = document) => scope.querySelectorAll('[data-g-catalog-facets]').forEach(start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
